import { QesColors } from "@app/shared/qes-standards/colors";
import { FC, useMemo } from "react";

interface DonutSegment {
  // The color of the pie segment and associated legend value + color swatch
  backgroundColor: QesColors | string;
  // A visual description of the segment, used in metadata
  description: string;
  // A label for the segment, used in metadata and the legend
  label: string;
  // The numerical value of the segment, used in metadata and the legend
  numberValue: number;
}

export interface DonutProps {
  // A visual description of the pie chart, used in metadata
  description: string;
  // The number shown in the center of the pie
  // innerLabelNumber?: number;
  // The label that goes with the number in the center of the pie
  innerLabelString?: string;
  // The segments of the pie chart
  segments: DonutSegment[];
  // The title of the chart, used in metadata
  title: string;
}

const sharedCircleProps = {
  cx: 21,
  cy: 21,
  r: 15.91549430918954,
};

/**
 * A donut chart! This accepts any number of segments. If they add up to less than 100, a gray background is shown. 
 * If they add up to more than 100, they'll start overlapping. The first segment starts at the top center. 
 * Segments smaller than 3% will be rendered as 3%.
 */
export const MetricsDonut: FC<DonutProps> = (props: DonutProps) => {
  const initialDashOffset = 25;
  let sumOfPreviousSegmentWidths = 0;
  const sumOfSegmentValues = useMemo(() => {
    return props.segments.reduce((result, value) => result += value.numberValue, 0);
  }, [props.segments]);

  return (
    <div className="metrics-donut__container">
      <figure>
        <div className="metrics-donut__content">
          {/* svg donut made with assistance and explanation from
            https://heyoka.medium.com/scratch-made-svg-donut-pie-charts-in-html5-2c587e935d72
          */}
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 42 42"
            className="metrics-donut__donut"
            role="img"
          >
            <title>{props.title}</title>
            <desc>{props.description}</desc>
            <circle
              className="metrics-donut__donut-hole"
              {...sharedCircleProps}
              fill={QesColors.QesPrimaryWhite}
              role="presentation"
            />
            <circle
              className="metrics-donut__donut-ring"
              {...sharedCircleProps}
              fill="transparent"
              stroke={QesColors.QesPrimaryGray3}
              strokeWidth="3"
              role="presentation"
            />

            {props.segments.map((segment, index) => {
              const previousSegmentWidths = sumOfPreviousSegmentWidths;
              const currentWidth = Math.max((segment.numberValue / sumOfSegmentValues) * 100, 3);
              sumOfPreviousSegmentWidths += currentWidth;
              return (
                <circle
                  key={segment.label}
                  className="metrics-donut__segment"
                  {...sharedCircleProps}
                  strokeWidth="3"
                  fill="transparent"
                  stroke={segment.backgroundColor}
                  strokeDasharray={`${currentWidth} ${100 - currentWidth}`}
                  strokeDashoffset={100 - previousSegmentWidths + initialDashOffset}
                  data-testid={`metrics-donut__segment--${index}`}
                >
                  <title>{segment.label}: {segment.numberValue}</title>
                  <desc>{segment.description}</desc>
                </circle>
              )
            })}
            <g className="metrics-donut__text">
              <text x="50%" y="50%">
                {(sumOfSegmentValues).toLocaleString()}
              </text>
              <text x="50%" y="50%">
                {props.innerLabelString}
              </text>
            </g>
          </svg>
        </div>
      </figure>

      <div className="metrics-donut__legend">
        {props.segments.map((segment) => {
          return (
            <span className="metrics-donut__legend-row" key={segment.label}>
              <span className="metrics-donut__legend-labels">
                <span className="metrics-color-swatch" style={{ backgroundColor: segment.backgroundColor }} />
                {segment.label}
              </span>
              <span className="metrics-donut__legend-values">
                <span style={{ color: segment.backgroundColor }}>{segment.numberValue.toLocaleString()}</span>
                <span>{((segment.numberValue / sumOfSegmentValues) * 100).toFixed(1)}%</span>
              </span>
            </span>
          )
        })}
      </div>
    </div>
  )
}